import {
    InputWysiwygComponent,
    validatorWysiwygRequired,
} from './../../../../../shared/components/inputs/input-wysiwyg/input-wysiwyg.component';
import { Component, computed, effect, inject, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { fb, isAllowed, routeParams, router, translate } from '../../../../../shared/shared.global-service';
import { Validators } from '@angular/forms';
import {
    allowedServicePatternByExpertise,
    ServicePatternStructureUsageEnum,
    servicePatternStructureUsageEnumOptions,
    servicePatternStructureUsagePatternGroup,
} from '@libs/types/src/services/enum/service-pattern-structure-usage.enum';
import { ServicePatternsStructurePageStore } from './services-patterns-structure-page.store';
import { isInvalid } from '../../../../../shared/helpers/validate';
import {
    ServicePatternStructureCreateInput,
} from '@libs/types/src/services/input-type/services/service-pattern.create-input';
import { ActivatedRoute } from '@angular/router';
import { SubscriptionComponent } from '../../../../../shared/helpers/subscription.component';
import { FilterBuilder } from '../../../../helper/filter.builder';
import { dateify, dateifyOrNow } from '../../../../../shared/helpers/date.helper';
import { BreadcrumbService } from 'xng-breadcrumb';
import {
    ServicePatternStructureUpdateInput,
} from '@libs/types/src/services/input-type/services/service-pattern.update-input';
import { BreadcrumbsEnum } from 'packages/client/src/app/modules/shared/models/breadcrumbs.enum';
import { OrganizationGlobalStore } from '../../../../../../store/organization.global-store';
import { InfiniteScrollStore } from '../../../../../shared/store/common/infinite-scroll.store';
import { contentGlobalSuccess } from '../../../../content.error';
import { getProceduresFrag, getServiceAnnouncementTypesFrag } from './services-patterns-structure-page.query';
import { ServiceTypeEnum } from '@libs/types/src/services/enum/service-type.enum';
import {
    SERVICE_SETTINGS_BASIC_SOCIAL_CONSULTING_KEY,
    SERVICE_SETTINGS_INDIVIDUAL_PLAN_TYPE_KEY,
    SERVICE_SETTINGS_MOVEMENT_RESTRICTION_TYPE_KEY,
    SERVICE_SETTINGS_SANCTION_TYPE_KEY,
    SERVICE_SETTINGS_SERVICE_ANNOUNCEMENT_TYPE_KEY,
    SERVICE_SETTINGS_SUGGESTION_TYPE_KEY,
    SERVICE_SETTINGS_TANGIBLE_DEPOSITS_TYPE_KEY,
} from '@libs/types/src/services/types/common-service-settings.type';
import { CardComponent } from '../../../../../../components/kit/card/card.component';
import { SelectInputComponent } from '../../../../../../components/kit/input/select-input/select-input.component';
import { TextInputComponent } from '../../../../../../components/kit/input/text-input/text-input.component';
import { FormatServiceProcedurePipe } from '../../../../../shared/pipes/format-service-procedure.pipe';
import { DateInputComponent } from '../../../../../../components/kit/input/date-input/date-input.component';
import { TranslatePipe } from '@ngx-translate/core';
import { ActionButtonsComponent } from '../../../../../../components/kit/action-buttons/action-buttons.component';
import {
    PerformedProceduresComponent,
} from '../../../../../shared/components/kit/performed-procedures/performed-procedures.component';
import {
    TransformSingleServiceProcedurePipe,
} from '../../../../../shared/components/kit/performed-procedures/performed-procedures.pipes';
import { featureOn } from '../../../../../../feature-flag';
import {
    WysiwygPlaceholderInfoComponent,
} from '../../../../../../components/kit/wysiwyg/wysiwyg-placeholder-info/wysiwyg-placeholder-info.component';
import { WysiwygPlaceholderGroupEnum } from '@libs/types/src/common/enum/wysiwyg-placeholder.enum';
import { changeSignal } from '../../../../../../utils/form.utils';
import { SOCIAL_SERVICE_CONTRACTS_KEY } from '@libs/types/src/services/types/social-service-settings-contracts.type';
import { isDev } from '../../../../../../domain-dependency.service';
import { ButtonComponent } from '../../../../../../components/kit/button/button.component';
import { WarnFloatMessageComponent } from '../../../../../../components/kit/float-message/warn-float-message.component';
import {
    ServiceAnnouncementTypeObject,
} from '@libs/types/src/services/object-type/service/social-service/service-announcement-type.object';
import {
    REMEMBRANCE_INVITATION_DEFAULT_PATTERN,
} from '@libs/types/src/services/constant/remembrance-invitation-default-pattern.constant';
import {
    getSocialStatisticGroupByType,
    SocialStatisticsTypeEnum,
} from '@libs/types/src/services/enum/statistics/social-statistics-type.enum';

@Component({
    selector: 'app-services-patterns-structure-page',
    templateUrl: './services-patterns-structure-page.component.html',
    styleUrl: './services-patterns-structure-page.component.scss',
    providers: [ServicePatternsStructurePageStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CardComponent,
        SelectInputComponent,
        TextInputComponent,
        FormatServiceProcedurePipe,
        DateInputComponent,
        TranslatePipe,
        InputWysiwygComponent,
        ActionButtonsComponent,
        PerformedProceduresComponent,
        TransformSingleServiceProcedurePipe,
        WysiwygPlaceholderInfoComponent,
        ButtonComponent,
        WarnFloatMessageComponent,
    ],
})
export class ServicesPatternsStructurePageComponent extends SubscriptionComponent {

    protected readonly store = inject(ServicePatternsStructurePageStore);
    protected readonly organizationalStore = inject(OrganizationGlobalStore);
    protected readonly route = inject(ActivatedRoute);
    private readonly breadcrumbs = inject(BreadcrumbService);

    public readonly wysiwyg = viewChild<InputWysiwygComponent>(InputWysiwygComponent);

    protected form = fb.group({
        usage: fb.control<string | null>(null, Validators.required),
        name: fb.control<string>('', Validators.required),
        procedureCode: fb.control<string | null>(null),
        validFrom: fb.control<Date>(new Date(), Validators.required),
        validTo: fb.control<Date | null>(null),
        note: fb.control<string>(''),
        content: fb.control<string>('', validatorWysiwygRequired),
    });

    protected title = 'service.patterns.structure.add.title';

    protected usageChange = changeSignal(this.form.controls.usage);
    protected service = this.organizationalStore.getServiceBySignalId(routeParams.serviceId);
    protected patternGroups = computed(() =>
        (servicePatternStructureUsagePatternGroup as any)[this.usageChange() || ''] ?? [WysiwygPlaceholderGroupEnum.SERVICE],
    );
    protected servicePatternTypes = computed(() => {
        const service = this.service();
        if (!service) return [];
        const customPatterns: any[] = [];

        if (service.type === ServiceTypeEnum.HEALTH_CARE) {
            const expertiseCode = service.expertise?.code ?? '0';
            const allowed = allowedServicePatternByExpertise(expertiseCode);

            return servicePatternStructureUsageEnumOptions.filter((it) => {
                return allowed.includes(String(it.value));
            });
        } else if (service.type === ServiceTypeEnum.SOCIAL) {
            const patterns: string[] = [ServicePatternStructureUsageEnum.CONTINUOUS_RECORD];

            if (featureOn.NEW_CALENDAR) {
                patterns.push(ServicePatternStructureUsageEnum.OPERATIONAL_WORK);
            }

            const restrictionSettings = JSON.parse(service.serviceSettings.find((it) => it.key === SERVICE_SETTINGS_MOVEMENT_RESTRICTION_TYPE_KEY)?.data || '{}');
            if (restrictionSettings?.useCard ?? true) {
                patterns.push(
                    ServicePatternStructureUsageEnum.MOVEMENT_RESTRICTION_PHYSICAL,
                    ServicePatternStructureUsageEnum.MOVEMENT_RESTRICTION_SAFE_ROOM,
                    ServicePatternStructureUsageEnum.MOVEMENT_RESTRICTION_MEDICAL_SUPERVISION,
                );
            }

            const sanctionSettings = JSON.parse(service.serviceSettings.find((it) => it.key === SERVICE_SETTINGS_SANCTION_TYPE_KEY)?.data || '{}');
            if (sanctionSettings?.useCard ?? true) {
                patterns.push(
                    ServicePatternStructureUsageEnum.SANCTION_DATA,
                    ServicePatternStructureUsageEnum.SANCTION_REASON,
                );
            }

            const individualPlanSettings = JSON.parse(service.serviceSettings.find((it) => it.key === SERVICE_SETTINGS_INDIVIDUAL_PLAN_TYPE_KEY)?.data || '{}');
            if (individualPlanSettings?.useCard ?? true) {
                patterns.push(
                    ServicePatternStructureUsageEnum.INDIVIDUAL_PLAN_SITUATION,
                    ServicePatternStructureUsageEnum.INDIVIDUAL_PLAN_PROCESS,
                    ServicePatternStructureUsageEnum.INDIVIDUAL_PLAN_EVALUATION,
                    ServicePatternStructureUsageEnum.INDIVIDUAL_WORK_RECORD,
                    ServicePatternStructureUsageEnum.INDIVIDUAL_WORK_RECORD_OPERATION,
                    ServicePatternStructureUsageEnum.INDIVIDUAL_WORK_RECORD_INTERVENTION,
                    ServicePatternStructureUsageEnum.INDIVIDUAL_WORK_NOTIFY_OPERATIONAL_RECORD,
                    ServicePatternStructureUsageEnum.GROUP_ACTIVITY_RECORD,
                    ServicePatternStructureUsageEnum.GROUP_ACTIVITY_RECORD_OPERATION,
                    ServicePatternStructureUsageEnum.GROUP_ACTIVITY_RECORD_INTERVENTION,
                    ServicePatternStructureUsageEnum.GROUP_ACTIVITY_NOTIFY_OPERATIONAL_RECORD,
                    ServicePatternStructureUsageEnum.MASS_WORK_RECORD,
                    ServicePatternStructureUsageEnum.MASS_WORK_RECORD_OPERATION,
                    ServicePatternStructureUsageEnum.MASS_WORK_RECORD_INTERVENTION,
                    ServicePatternStructureUsageEnum.MASS_WORK_NOTIFY_OPERATIONAL_RECORD,
                );
            }

            const contractsSettings = JSON.parse(service.serviceSettings.find((it) => it.key === SOCIAL_SERVICE_CONTRACTS_KEY)?.data || '{}');
            if (contractsSettings?.useCard ?? true) {
                patterns.push(
                    ServicePatternStructureUsageEnum.CONTRACT_ATTACHMENT_PRICE_LIST,
                    ServicePatternStructureUsageEnum.CONTRACT_ATTACHMENT_RULES,
                    ServicePatternStructureUsageEnum.CONTRACT_ATTACHMENT_IP,
                    ServicePatternStructureUsageEnum.CONTRACT_ATTACHMENT_OTHER,
                    ServicePatternStructureUsageEnum.CONTRACT_ADDENDUM_EXTENSION_REDUCTION,
                    ServicePatternStructureUsageEnum.CONTRACT_ADDENDUM_CHANGE_ACTIVITIES,
                    ServicePatternStructureUsageEnum.CONTRACT_ADDENDUM_CHANGE_PRICE,
                    ServicePatternStructureUsageEnum.CONTRACT_ADDENDUM_CHANGE_ACCOMMODATION,
                    ServicePatternStructureUsageEnum.CONTRACT_ADDENDUM_TRANSFER,
                    ServicePatternStructureUsageEnum.CONTRACT_ADDENDUM_OTHER,
                    ServicePatternStructureUsageEnum.CONTRACT_TEXT,
                );
            }

            if (service.social?.useWaitingList || false) {
                patterns.push(
                    ServicePatternStructureUsageEnum.WAITING_LIST_NOTE,
                );
            }

            const suggestionsSettings = JSON.parse(service.serviceSettings
                .find((it) => it.key === SERVICE_SETTINGS_SUGGESTION_TYPE_KEY)
                ?.data || '{}',
            );
            if (featureOn.SUGGESTION && (suggestionsSettings?.useCard ?? true)) {
                patterns.push(
                    ServicePatternStructureUsageEnum.SUGGESTION_SATISFACTION,
                    ServicePatternStructureUsageEnum.SUGGESTION_COMPLAINT,
                    ServicePatternStructureUsageEnum.SUGGESTION_FINDING,
                );
            }

            const basicSocialConsultingSettings = JSON.parse(service.serviceSettings
                .find((it) => it.key === SERVICE_SETTINGS_BASIC_SOCIAL_CONSULTING_KEY)
                ?.data || '{}',
            );
            if (basicSocialConsultingSettings?.useCard ?? true) {
                patterns.push(
                    ServicePatternStructureUsageEnum.BASIC_SOCIAL_CONSULTING_TEXT,
                );
            }

            const serviceAnnouncementSettings = JSON.parse(service.serviceSettings
                .find((it) => it.key === SERVICE_SETTINGS_SERVICE_ANNOUNCEMENT_TYPE_KEY)
                ?.data || '{}',
            );

            if (featureOn.SERVICE_ANNOUNCEMENT && (serviceAnnouncementSettings?.useCard ?? true)) {
                this.store.data.getServiceAnnouncementTypes()?.items?.forEach((it: ServiceAnnouncementTypeObject) => {
                    customPatterns.push({
                        value: ServicePatternStructureUsageEnum.SERVICE_ANNOUNCEMENT_TYPE + it.id,
                        name: `${translate.instant(`service.patterns.usage.${ServicePatternStructureUsageEnum.SERVICE_ANNOUNCEMENT_TYPE}`)}${it.name}`,
                    });
                });
            }

            const tangibleDepositsSettings = JSON.parse(service.serviceSettings
                .find((it) => it.key === SERVICE_SETTINGS_TANGIBLE_DEPOSITS_TYPE_KEY)
                ?.data || '{}',
            );
            if (featureOn.TANGIBLE_DEPOSITS && (tangibleDepositsSettings?.useCard ?? true)) {
                patterns.push(
                    ServicePatternStructureUsageEnum.TANGIBLE_DEPOSIT_RECEIPT,
                    ServicePatternStructureUsageEnum.TANGIBLE_DEPOSIT_RELEASE,
                );
            }

            if (this.isRemembranceInvitationAvailable(service.id)) {
                patterns.push(ServicePatternStructureUsageEnum.STATISTICS_REMEMBRANCE_INVITATION);
            }

            return [
                ...servicePatternStructureUsageEnumOptions.filter((it) => patterns.includes(it.value as any)),
                ...customPatterns,
            ];
        }

        return [];
    });

    protected proceduresScroll = computed(() => new InfiniteScrollStore(
        this.store.api.getServiceMedicalProcedures,
        FilterBuilder.singlePage({
            serviceId: routeParams.serviceId(),
            active: true,
        }),
        true,
        getProceduresFrag,
        undefined,
        true,
        'code',
    ));

    private structureId: number | null = null;

    constructor() {
        super();
        effect(() => {
            if (!isAllowed['service.update']()) {
                this.form.disable();
            } else {
                this.form.enable();
            }
        });

        effect(() => {
            this.structureId = Number(routeParams.patternId() || 0);
            if (this.structureId) {
                this.store.api.getServicePatterns(FilterBuilder.singleItem({ id: this.structureId }));
                this.title = 'service.patterns.structure.edit.title';
            }
        });

        effect(() => {
            this.store.api.getServiceAnnouncementTypes(FilterBuilder.filter({
                serviceId: String(routeParams.serviceId()),
                active: true,
            }), getServiceAnnouncementTypesFrag);
        });

        effect(() => {
            const structure = this.store.structure();
            if (structure) {
                this.breadcrumbs.set(`@${BreadcrumbsEnum.ServicePatternsStructures}`, `${BreadcrumbsEnum.ServicePatternsStructures}|${structure?.id}`);
                this.form.patchValue({
                    ...structure,
                    validFrom: dateifyOrNow(structure.validFrom),
                    validTo: dateify(structure.validTo),
                    procedureCode: structure.procedureCode || null,
                });
            }
        });

        effect(() => {
            if (this.hasHandlebarsBlocks()) {
                this.rawMode.set(true);
            }
        });

        effect(() => this.setDefaultValues());

        this.store.doOnActions(
            () => router.navigate(['../../'], { relativeTo: this.route }),
            'createServicePatternStructure',
            'updateServicePatternStructure',
        );
    }

    async submit(): Promise<void> {
        if (isInvalid(this.form)) {
            return;
        }

        const base = {
            ...this.form.value,
            procedureCode: this.form.value.procedureCode,
            validFrom: this.form.value.validFrom?.toISOString(),
            validTo: this.form.value.validTo?.toISOString() || null,
        };

        if (this.structureId) {
            const data = base as ServicePatternStructureUpdateInput;
            data.id = this.structureId;
            await this.store.api.updateServicePatternStructure(data);
            this.wysiwyg()?.resetBackupValues();

            contentGlobalSuccess.next('common.updated');
        } else {
            const data = base as ServicePatternStructureCreateInput;
            data.serviceId = Number(routeParams.serviceId());
            await this.store.api.createServicePatternStructure(data);
            this.wysiwyg()?.resetBackupValues();

            contentGlobalSuccess.next('common.created');
        }
    }

    private isRemembranceInvitationAvailable(serviceId: number): boolean {
        const statisticsType = SocialStatisticsTypeEnum.DECEASED_CLIENT_CONTACTS;
        const group = getSocialStatisticGroupByType(statisticsType);
        const enabledServiceIds = this.store.globalGlobalStore.socialServiceStore
            .allowStatistics()[group]?.[statisticsType] ?? [];

        return featureOn.SOCIAL_STATISTICS && isAllowed['statistics.read']() && enabledServiceIds.includes(serviceId);
    }

    private setDefaultValues(): void {
        if (this.structureId) return;

        const usage = this.usageChange();
        const content = this.contentChange() ?? '';
        if (!content) {
            switch (usage) {
                case ServicePatternStructureUsageEnum.STATISTICS_REMEMBRANCE_INVITATION:
                    this.form.controls.content.setValue(REMEMBRANCE_INVITATION_DEFAULT_PATTERN);
                    break;
            }
        }
    }

    protected readonly rawMode = signal(false);

    private readonly contentChange = toSignal(this.form.controls.content.valueChanges, { initialValue: '' });

    protected readonly hasHandlebarsBlocks = computed(() =>
        !!routeParams.patternId() &&
        (this.contentChange() ?? '').includes('{{#'),
    );

    protected readonly ServicePatternStructureUsageEnum = ServicePatternStructureUsageEnum;

    protected readonly ServiceTypeEnum = ServiceTypeEnum;
    protected readonly isDev = isDev;
}
